import {useState} from 'react';
import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import {Detail, EMPHASIS, Label, Pill, PillVariant, Text} from '@forecast-it/design-system';
import type {ActivityTimelineItem_activity$key} from './__generated__/ActivityTimelineItem_activity.graphql';
import {ActivityBadge, kindForEventType, type ActivityKind} from './ActivityBadge';
import {MessageBubble} from './MessageBubble';
import {EventPill} from './EventPill';
import {CopyLinkButton} from './CopyLinkButton';
import {MentionText} from './MentionText';
import {LabelledButton} from './LabelledButton';
import {formatCreated} from '../utils/formatCreated';
import {ticketingAttachmentDownloadUrl} from '../../../graphql/forecastRelay';

// One entry: the rail badge, then everything about the entry beside it. The badge
// is a sibling rather than a child of the body so the rail line, drawn by the
// timeline behind this row, lines up with every entry regardless of its height.
// `deep-link-highlight` is a kept animation from index.css, applied when an entry
// is the target of a copied link.
const Entry = styled.div`
	position: relative;
	z-index: 1;
	display: flex;
	align-items: flex-start;
	gap: ${p => p.theme.sys.spacing[400]};
`;
const Body = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[100]};
	flex: 1 1 0%;
	min-width: 0;
	padding-top: 3px;
`;
const HeaderRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;
// The direction tag is type, not a pill: an email's direction is a fact about the
// entry rather than a status worth a filled shape.
const DirectionTag = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
// The card holds the message itself. A left edge would compete with the rail, so
// the border is even and the direction is carried by the tag above it.
const Card = styled.div`
	position: relative;
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	border-radius: ${p => p.theme.sys.size.borderradius['2']};
	padding: ${p => p.theme.sys.spacing[300]} ${p => p.theme.sys.spacing[400]};
	background: ${p => p.theme.sys.color.background.neutral['1']};
`;
const CardStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[100]};
`;
const MetaLine = styled.div`
	display: flex;
	align-items: baseline;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const MetaKey = styled.div`
	flex: none;
	width: 44px;
`;
const PillRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const AttachmentLink = styled.a`
	text-decoration: none;
`;

const TimelineItemFragment = graphql`
	fragment ActivityTimelineItem_activity on ActivityEvent {
		__typename
		... on MessageActivity {
			id
			createdAt
			body
			bodyHtml
			direction
			authorType
			author {
				id
				fullName
			}
			deliveryStatus
			retryCount
			toEmails
			ccEmails
			attachments {
				id
				filename
				contentType
				sizeBytes
				inlineContentId
				downloadUrl
			}
		}
		... on InternalCommentActivity {
			id
			createdAt
			body
			author {
				id
				fullName
			}
			mentionedUsers {
				id
				fullName
			}
		}
		... on EventActivity {
			id
			createdAt
			eventType
			actor {
				id
				fullName
			}
			oldValue
			newValue
		}
		... on AttachmentActivity {
			id
			createdAt
			filename
			contentType
			sizeBytes
			downloadUrl
		}
	}
`;

interface Props {
	activity: ActivityTimelineItem_activity$key;
	onRetry?: (messageId: string) => void;
	isHighlighted?: boolean;
	/**
	 * The requester's email address. An inbound message carries its recipients but
	 * not its sender, so the "From" line can only be filled in by the caller, which
	 * knows whose ticket this is.
	 */
	requesterEmail?: string;
}

/** One aligned key/value line — quiet key in a fixed column, so the values lead. */
function EmailMetaLine({label, value}: {label: string; value: string | null | undefined}) {
	if (!value) return null;
	return (
		<MetaLine>
			<MetaKey>
				<Detail color="medium">{label}</Detail>
			</MetaKey>
			<Detail>{value}</Detail>
		</MetaLine>
	);
}

function formatSize(sizeBytes: number): string {
	return `${(sizeBytes / 1024).toFixed(1)} KB`;
}

/**
 * One entry on the ticket's activity rail.
 *
 * Every kind reads the same way: a badge on the rail, then a header saying who did
 * what and when, then the entry's substance. Emails and internal notes get a card
 * because they carry someone's words; a status change or an attachment is a single
 * line and gets none.
 *
 * An email's headers beyond the primary correspondent stay behind "Show full email" —
 * a timeline is skimmed far more often than it is read in full.
 */
export function ActivityTimelineItem({activity: activityRef, onRetry, isHighlighted, requesterEmail}: Props) {
	const event = useFragment(TimelineItemFragment, activityRef);
	const [showFullEmail, setShowFullEmail] = useState(false);
	const highlightClass = isHighlighted ? 'deep-link-highlight' : undefined;

	const header = (actor: string, createdAt: string, tag?: string, eventId?: string) => (
		<HeaderRow>
			<Label>{actor}</Label>
			{tag && <DirectionTag>{tag}</DirectionTag>}
			<Detail color="medium">{formatCreated(createdAt)}</Detail>
			{eventId && <CopyLinkButton eventId={eventId} />}
		</HeaderRow>
	);

	switch (event.__typename) {
		case 'MessageActivity': {
			const isOutbound = event.direction === 'outbound' || event.authorType === 'agent';
			const kind: ActivityKind = isOutbound ? 'emailOut' : 'emailIn';
			const messageLike = {
				id: event.id,
				event_type: 'message' as const,
				created_at: event.createdAt,
				author_type: (event.authorType === 'agent' ? 'agent' : 'requester') as 'agent' | 'requester',
				author_id: event.author?.id ?? null,
				body: event.body,
				body_html: event.bodyHtml ?? null,
				direction: (event.direction ?? null) as 'inbound' | 'outbound' | null,
				attachments: (event.attachments ?? []).map(a => ({
					id: a.id,
					filename: a.filename,
					size_bytes: a.sizeBytes,
					mime_type: a.contentType,
					inline_content_id: a.inlineContentId ?? null,
					downloadUrl: a.downloadUrl,
				})),
				delivery_status: (event.deliveryStatus ?? null) as 'queued' | 'sending' | 'sent' | 'failed' | null,
				retry_count: event.retryCount,
			};
			const authorLabel = event.author ? event.author.fullName ?? 'Deleted user' : isOutbound ? 'Agent' : 'Requester';
			const toLine = (event.toEmails ?? []).join(', ');
			const ccLine = (event.ccEmails ?? []).join(', ');
			// Inbound mail names its sender, outbound its recipients — whichever the
			// reader needs first sits above the body, the rest behind the reveal.
			const primary = isOutbound ? {label: 'To', value: toLine} : {label: 'From', value: requesterEmail};
			const rest = isOutbound
				? [
						{label: 'From', value: requesterEmail},
						{label: 'Cc', value: ccLine},
				  ]
				: [
						{label: 'To', value: toLine},
						{label: 'Cc', value: ccLine},
				  ];

			return (
				<Entry id={event.id} className={highlightClass}>
					<ActivityBadge kind={kind} />
					<Body>
						{header(authorLabel, event.createdAt as string, isOutbound ? 'Email sent' : 'Email received', event.id)}
						<Card data-testid="email-card">
							<CardStack>
								{/* The correspondent leads. The time is in the entry header above, so
								    the card does not repeat it. */}
								<EmailMetaLine label={primary.label} value={primary.value} />
								<MessageBubble event={messageLike} onRetry={onRetry} />
								{showFullEmail &&
									rest.map(r => <EmailMetaLine key={r.label} label={r.label} value={r.value} />)}
								<div>
									<LabelledButton
										emphasis={EMPHASIS.LOW}
										label={showFullEmail ? 'Hide email details' : 'Show full email'}
										aria-expanded={showFullEmail}
										onPress={() => setShowFullEmail(v => !v)}
									>
										{showFullEmail ? 'Hide details' : 'Show full email'}
									</LabelledButton>
								</div>
							</CardStack>
						</Card>
					</Body>
				</Entry>
			);
		}

		case 'InternalCommentActivity': {
			const authorLabel = event.author ? event.author.fullName ?? 'Deleted user' : 'Deleted user';
			return (
				<Entry id={event.id} className={highlightClass}>
					<ActivityBadge kind="note" />
					<Body>
						{header(authorLabel, event.createdAt as string, 'Internal note', event.id)}
						<Card data-testid="internal-note-card">
							<CardStack>
								<Detail color="medium">Only visible to your team</Detail>
								<Text size="2">
									<MentionText
										body={event.body}
										mentionedUsers={Object.fromEntries(
											(event.mentionedUsers ?? []).map(u => [u.id, u.fullName ?? 'Deleted user'])
										)}
									/>
								</Text>
							</CardStack>
						</Card>
					</Body>
				</Entry>
			);
		}

		case 'AttachmentActivity': {
			return (
				<Entry id={event.id} className={highlightClass}>
					<ActivityBadge kind="attachment" />
					<Body>
						{header('Attachment added', event.createdAt as string, undefined, event.id)}
						<PillRow>
							<AttachmentLink
								href={ticketingAttachmentDownloadUrl(event.downloadUrl)}
								target="_blank"
								rel="noopener noreferrer"
							>
								<Pill variant={PillVariant.NEUTRAL} icon="paperclip">
									{`${event.filename} (${formatSize(event.sizeBytes)})`}
								</Pill>
							</AttachmentLink>
						</PillRow>
					</Body>
				</Entry>
			);
		}

		case 'EventActivity': {
			const actorLabel = event.actor ? event.actor.fullName ?? 'Deleted user' : 'System';
			const eventLike = {
				id: event.id,
				event_type: event.eventType,
				created_at: event.createdAt,
				actor_id: event.actor?.id ?? null,
				actor_name: event.actor ? event.actor.fullName ?? 'Deleted user' : null,
				payload:
					event.oldValue != null || event.newValue != null
						? {from: event.oldValue ?? undefined, to: event.newValue ?? undefined}
						: null,
			};
			return (
				<Entry id={event.id} className={highlightClass}>
					<ActivityBadge kind={kindForEventType(event.eventType)} />
					<Body>
						{header(actorLabel, event.createdAt as string, undefined, event.id)}
						<EventPill event={eventLike as Parameters<typeof EventPill>[0]['event']} />
					</Body>
				</Entry>
			);
		}

		default:
			return null;
	}
}
